<template>
  <div class="pa-4">
    <v-card flat>
      <v-card-text>
        <v-row>
          <v-col cols="10">
            <v-autocomplete v-show="!announcement || !newAnnouncement ? true : false" @change="getAnnouncementData()"
              v-model="selectedAnnouncementId" :items="groupedAnnouncements" item-text="message"
              item-value="_id" :label="$t('message.announcements_available')" outlined dense
              clearable></v-autocomplete>
          </v-col>
          <v-col cols="2" class="text-right">
            <v-btn v-show="!announcement || !newAnnouncement ? true : false" color="primary" outlined
              @click="startNew()">{{ $t('message.add') }}</v-btn>
            <v-btn elevation="0" color="error" v-show="newAnnouncement ? true : false" outlined
              @click="newAnnouncement = false; announcement = null">{{ $t('message.cancel') }}</v-btn>
          </v-col>
        </v-row>
      </v-card-text>
      <div v-if="loading">
        <v-skeleton-loader type="card, article, actions"></v-skeleton-loader>
      </div>
      <v-card-text v-else-if="announcement" class="flex flex-col" style="gap: 1rem">
        <v-text-field outlined dense :label="$t('message.announcement_message')" v-model="announcement.message"
          counter="120" maxlength="120"></v-text-field>
        <v-textarea outlined dense rows="3" :label="$t('message.announcement_text')" v-model="announcement.text"
          counter="750" maxlength="750"></v-textarea>
        <div class="flex align-center" style="gap: 1rem">
          <v-avatar v-if="announcement.image" size="64" tile>
            <ImageDisplay :src="announcement.image" style="max-width: 64px; max-height: 64px" />
          </v-avatar>
          <PlusImageUploadCroppie :label="$t('message.announcements_upload_image')"
            @uploaded="onImageUploaded"></PlusImageUploadCroppie>
        </div>
        <div>
          <div class="plusTextSecondary--text" style="font-size: 0.875rem; margin-bottom: 0.25rem">
            {{ $t('message.announcements_expires_on') }}
          </div>
          <DatePicker v-model="announcement.expiresOn" :past-event="false"></DatePicker>
          <div class="text-caption mt-1">{{ $t('message.announcements_expires_on_explained') }}</div>
        </div>
        <v-switch hide-details v-model="announcement.active" :label="$t('message.active')"></v-switch>
      </v-card-text>
      <v-card-actions v-if="announcement">
        <v-btn block @click="addNewAnnouncement" v-if="newAnnouncement" outlined color="primary">{{ $t('message.add')
        }}</v-btn>
        <div v-else class="flex flex-col" style="width: 100%; gap: 0.5rem">
          <v-btn block @click="updateAnnouncement" outlined color="primary">{{ $t('message.update') }}</v-btn>
          <v-btn block @click="deleteAnnouncement" outlined color="error">{{ $t('message.delete') }}</v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import Api from "@/services/Api";
import ImageDisplay from "@/components/displays/ImageDisplay";
import PlusImageUploadCroppie from '@/components/plus/PlusImageUploadCroppie';
import DatePicker from "@/components/inputs/DateTimePicker.vue";

export default {
  components: {
    ImageDisplay,
    PlusImageUploadCroppie,
    DatePicker,
  },
  data() {
    return {
      loading: false,
      announcements: [],
      selectedAnnouncementId: null,
      announcement: null,
      newAnnouncement: false,
    };
  },
  created() {
    this.getAnnouncements();
  },
  computed: {
    groupedAnnouncements() {
      const active = this.announcements.filter(a => a.active);
      const inactive = this.announcements.filter(a => !a.active);
      const result = [];
      if (active.length) {
        result.push({ header: this.$t('message.active') });
        result.push(...active);
      }
      if (inactive.length) {
        result.push({ header: this.$t('message.inactive') });
        result.push(...inactive);
      }
      return result;
    },
  },
  methods: {
    startNew() {
      this.selectedAnnouncementId = null;
      this.newAnnouncement = true;
      this.announcement = {
        message: '',
        text: '',
        image: null,
        expiresOn: null,
        active: true,
      };
    },
    getAnnouncements() {
      this.loading = true;
      Api()
        .get('admins/academy/' + this.$store.state.managedAcademy + '/announcements')
        .then((response) => {
          this.announcements = response.data.announcements || [];
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getAnnouncementData() {
      this.newAnnouncement = false;
      if (!this.selectedAnnouncementId) {
        this.announcement = null;
        return;
      }
      const found = this.announcements.find(a => a._id === this.selectedAnnouncementId);
      this.announcement = found ? { ...found } : null;
    },
    onImageUploaded(uploadedFileData) {
      if (!uploadedFileData) return;
      this.announcement.image = uploadedFileData.location;
    },
    addNewAnnouncement() {
      Api()
        .post('admins/academy/' + this.$store.state.managedAcademy + '/announcements', this.announcement)
        .then(() => {
          this.newAnnouncement = false;
          this.announcement = null;
          this.getAnnouncements();
          this.showAlert('success', this.$t('message.success'));
        })
        .catch(() => {
          this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    updateAnnouncement() {
      Api()
        .patch(
          'admins/academy/' + this.$store.state.managedAcademy + '/announcements/' + this.announcement._id,
          this.announcement,
        )
        .then(() => {
          this.getAnnouncements();
          this.showAlert('success', this.$t('message.updated'));
        })
        .catch(() => {
          this.showAlert('error', this.$t('message.error_generic'));
        });
    },
    deleteAnnouncement() {
      Api()
        .delete('admins/academy/' + this.$store.state.managedAcademy + '/announcements/' + this.announcement._id)
        .then(() => {
          this.selectedAnnouncementId = null;
          this.announcement = null;
          this.getAnnouncements();
          this.showAlert('success', this.$t('message.deleted'));
        })
        .catch(() => {
          this.showAlert('error', this.$t('message.error_generic'));
        });
    },
  },
};
</script>

<style scoped></style>
